import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { usePromptLibraryFacade } from '../../../apis/facades/usePromptLibraryFacade';
import {
  setLoading,
  setPromptGroups,
  setPromptGroups_deprecated,
  addEmptyPromptGroup_deprecated as addEmptyPromptGroupAction,
  removeEmptyPromptGroup_deprecated as removeEmptyPromptGroupAction,
  addPromptGroup_deprecated,
  updatePromptGroup as updatePromptGroupAction,
  deletePromptGroup,
  setSelectedPromptGroup_deprecated,
  resetState as resetStateAction,
  addPromptGroup,
} from '../../../reducers/promptLibraryReducer';
import { PromptGroup_deprecated, PromptGroup } from '@server/prompts';
import { telemetry } from '../../../telemetry';
import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import { undo } from '../../../reducers/undoable';
import { useSweepNotifications } from '../../notifications/useSweepNotifications';

export const usePromptGroups = () => {
  const dispatch = useDispatch();
  const { addNotification } = useSweepNotifications();
  const {
    get_promptGroups,
    get_promptGroups_deprecated,
    create_promptGroup,
    update_promptGroup,
    delete_promptGroup,
  } = usePromptLibraryFacade();

  // New: fetches groups only (without nested prompts)
  const loadPromptGroups = useCallback(async () => {
    try {
      dispatch(setLoading({ isLoading: true }));
      const groups = await get_promptGroups();
      dispatch(setPromptGroups({ promptGroups: groups }));
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      dispatch(setLoading({ isLoading: false }));
    }
  }, [dispatch, get_promptGroups]);

  // Deprecated: fetches groups with nested prompts
  const loadPromptGroups_deprecated = useCallback(async () => {
    try {
      dispatch(setLoading({ isLoading: true }));
      const groups = await get_promptGroups_deprecated();
      dispatch(setPromptGroups_deprecated({ promptGroups: groups }));
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      dispatch(setLoading({ isLoading: false }));
    }
  }, [dispatch, get_promptGroups_deprecated]);

  const handleError = useCallback(
    (error: any, errorMessage: string, optimisticAction?: any) => {
      addNotification({
        message: errorMessage,
        variant: SweepNotificationVariant.Error,
      });
      dispatch(undo(optimisticAction));
      telemetry.captureError(errorMessage, error);
    },
    [addNotification, dispatch],
  );

  const addEmptyPromptGroup__deprecated = useCallback(() => {
    dispatch(addEmptyPromptGroupAction());
  }, [dispatch]);

  const removeEmptyPromptGroup__deprecated = useCallback(() => {
    dispatch(removeEmptyPromptGroupAction());
  }, [dispatch]);

  const createPromptGroup = useCallback(
    async (groupData: PromptGroup) => {
      try {
        const newGroup = await create_promptGroup(groupData);
        dispatch(addPromptGroup({ promptGroup: newGroup }));
        return newGroup;
      } catch (error) {
        handleError(error, 'Failed to create prompt category');
        return null;
      }
    },
    [dispatch, handleError, create_promptGroup],
  );

  const createPromptGroup__deprecated = useCallback(
    async (groupData: PromptGroup) => {
      try {
        const newGroup = await create_promptGroup(groupData);
        dispatch(addPromptGroup_deprecated({ promptGroup: { ...newGroup, prompts: [] } }));
        return newGroup;
      } catch (error) {
        handleError(error, 'Failed to create prompt category');
        return null;
      }
    },
    [dispatch, handleError, create_promptGroup],
  );

  const updatePromptGroup = useCallback(
    async (promptGroup: PromptGroup) => {
      const groupId = promptGroup.id;
      const optimisticAction = updatePromptGroupAction({ id: groupId, update: promptGroup });
      try {
        dispatch(optimisticAction);
        await update_promptGroup(groupId, promptGroup);
      } catch (error) {
        handleError(error, 'Failed to update prompt category', optimisticAction);
      }
    },
    [dispatch, handleError, update_promptGroup],
  );

  const removePromptGroup = useCallback(
    async (groupId: string) => {
      const optimisticAction = deletePromptGroup({ groupId });
      try {
        dispatch(optimisticAction);
        if (!groupId) return;
        await delete_promptGroup(groupId);
      } catch (error) {
        handleError(error, 'Failed to delete prompt category', optimisticAction);
      }
    },
    [dispatch, handleError, delete_promptGroup],
  );

  const pinGroup = useCallback(
    async (groupId: string, isPinned: boolean = true) => {
      const optimisticAction = updatePromptGroupAction({ id: groupId, update: { isPinned } });
      try {
        dispatch(optimisticAction);
        await update_promptGroup(groupId, { isPinned });
      } catch (error) {
        handleError(error, 'Failed to pin/unpin group', optimisticAction);
      }
    },
    [dispatch, update_promptGroup, handleError],
  );

  const selectGroup = useCallback(
    (group: PromptGroup_deprecated | null) => {
      dispatch(setSelectedPromptGroup_deprecated({ group }));
    },
    [dispatch],
  );

  const unSelectGroup = useCallback(() => {
    dispatch(setSelectedPromptGroup_deprecated({ group: null }));
  }, [dispatch]);

  const resetState = useCallback(() => {
    dispatch(resetStateAction());
  }, [dispatch]);

  return {
    loadPromptGroups,
    loadPromptGroups_deprecated,
    createPromptGroup,
    createPromptGroup__deprecated,
    updatePromptGroup,
    removePromptGroup,
    pinGroup,
    addEmptyPromptGroup__deprecated,
    removeEmptyPromptGroup__deprecated,

    selectGroup,
    unSelectGroup,
    resetState,
  };
};
